/**
 * Turn geographic targeting into the SVG primitives the map draws — pure, so it is unit-tested
 * without rendering.
 *
 * Everything is pre-projected into one national viewBox (see geo-types). This resolves the targeted
 * entities to their pre-projected shapes/points, then ZOOMS by returning an `<svg>` viewBox tight to
 * their bounding box (padded) — so a ZIP campaign fills the frame with its circles the way the mockup
 * does, while a Country report shows the whole US. Nothing here fetches or projects at runtime.
 */

import { GEO_BASE } from './geo/geo-base.js';
import { GEO_ZIPS } from './geo/geo-zips.js';
import { GEO_COUNTIES } from './geo/geo-counties.js';
import { GEO_CITIES } from './geo/geo-cities.js';
import { GEO_DMA } from './geo/geo-dma.js';

export type GeoMode = 'country' | 'state' | 'zip' | 'city' | 'county' | 'dma';
export const GEO_MODES: readonly GeoMode[] = ['country', 'state', 'zip', 'city', 'county', 'dma'];

export interface GeoInput {
  zips?: string[];
  radiusMiles?: number;
  states?: string[];   // USPS abbreviation, FIPS, or full name
  counties?: string[]; // 5-digit county FIPS
  cities?: string[];   // "City, ST" or "City|ST"
  dmas?: string[];     // DMA code or name (rendered as its member counties)
}

export interface GeoRender {
  viewBox: string;
  /** State outlines drawn faintly for context under every mode. */
  basePaths: string[];
  /** County boundaries that fall in view — faint reference lines, the way the mockup's map shows
   *  minor roads. Only populated once zoomed in enough to be legible. */
  countyLines: string[];
  /** Filled red regions (state / county / dma). */
  fills: string[];
  /** The bold red national outline (country mode only). */
  nationOutline?: string;
  /** Red radius circles (zip / city). */
  circles: Array<{ cx: number; cy: number; r: number }>;
  /** The largest cities in view, labelled for reference (dot + name). */
  labels: Array<{ x: number; y: number; text: string }>;
  /** Label font size and city-dot radius in user units, scaled to the zoom so they read the same. */
  fontSize: number;
  dotR: number;
  /** How many targeted entities were requested and how many resolved to a shape/point. */
  requested: number;
  resolved: number;
}

/** "City|ST" → [x,y], built once from the population-sorted city list, for City targeting lookups. */
const CITY_INDEX = new Map<string, [number, number]>();
for (const [name, st, x, y] of GEO_CITIES.cities) {
  const k = `${name}|${st}`.toLowerCase();
  if (!CITY_INDEX.has(k)) CITY_INDEX.set(k, [x, y]);
}

export interface ScopedCity { city: string; state: string; impressions: number }

/**
 * Split delivery cities into those INSIDE the campaign's local target and the rest.
 *
 * For ZIP targeting a city is inside when its position is within `radiusMiles` of any targeted ZIP —
 * the same circles the map draws — which keeps the breakdown to the real coverage area rather than the
 * IP→location resolution artifacts in other states. `states` (USPS) scopes state/multi-state targeting.
 * `country`/no targeting returns everything. Cities with no known coordinates fall back to the state
 * test, so a small in-area town is not dropped just because it is not in the city index.
 */
export function scopeCitiesToTarget<T extends ScopedCity>(
  cities: readonly T[],
  input: { geoMode?: string; zips?: string[]; radiusMiles?: number; states?: string[] },
): { inside: T[]; outsideTotal: number } {
  const targetStates = new Set((input.states ?? []).map((s) => s.toUpperCase()));
  const pts = (input.zips ?? []).map((z) => GEO_ZIPS.zipXY[String(z).trim()]).filter(Boolean) as Array<[number, number]>;
  // If the campaign has no local targeting to scope by, everything is "inside".
  if (!pts.length && targetStates.size === 0) return { inside: [...cities], outsideTotal: 0 };
  const r = (input.radiusMiles && input.radiusMiles > 0 ? input.radiusMiles : 10) * GEO_BASE.milesToPx;
  const r2 = r * r;
  // When ZIP-scoped, the target states are those the ZIPs resolve to — the fallback for coordinate-less cities.
  const zipStateFallback = new Set<string>();
  const inside: T[] = [];
  let outsideTotal = 0;
  for (const c of cities) {
    const ll = CITY_INDEX.get(`${c.city}|${c.state}`.toLowerCase());
    let hit = false;
    if (pts.length && ll) {
      hit = pts.some(([x, y]) => { const dx = x - ll[0], dy = y - ll[1]; return dx * dx + dy * dy <= r2; });
      if (hit) zipStateFallback.add(c.state.toUpperCase());
    }
    if (!hit && targetStates.size) hit = targetStates.has(c.state.toUpperCase());
    if (hit) inside.push(c); else outsideTotal += Number(c.impressions) || 0;
  }
  // Second pass: ZIP-scoped cities with no coordinates, but in a state the ZIPs clearly cover, are kept
  // rather than dropped into "Other" — so small in-area towns still appear.
  if (pts.length && zipStateFallback.size) {
    const insideSet = new Set(inside);
    let moved = 0;
    for (const c of cities) {
      if (insideSet.has(c)) continue;
      if (!CITY_INDEX.get(`${c.city}|${c.state}`.toLowerCase()) && zipStateFallback.has(c.state.toUpperCase())) {
        inside.push(c); moved += Number(c.impressions) || 0;
      }
    }
    outsideTotal -= moved;
  }
  return { inside, outsideTotal };
}

type BBox = [number, number, number, number]; // [minX, minY, maxX, maxY]

/** Bounding box of an integer M/L/Z path `d`. */
export function pathBBox(d: string): BBox | null {
  const nums = d.match(/-?\d+/g);
  if (!nums || nums.length < 2) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = +nums[i], y = +nums[i + 1];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}

function merge(a: BBox | null, b: BBox | null): BBox | null {
  if (!a) return b; if (!b) return a;
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])];
}

/** A padded viewport [x, y, w, h] for the targeted region, clamped to the canvas and floored to a
 *  minimum span. `minSpan` is small for point modes (zip/city) so they zoom in close, larger for
 *  region modes so a small state/county still shows its surroundings. Falls back to the whole US. */
function viewportFor(bbox: BBox | null, W: number, H: number, minSpan: number): [number, number, number, number] {
  if (!bbox) return [0, 0, W, H];
  let [minX, minY, maxX, maxY] = bbox;
  const padX = Math.max((maxX - minX) * 0.14, minSpan / 2 - (maxX - minX) / 2, 4);
  const padY = Math.max((maxY - minY) * 0.14, minSpan / 2 - (maxY - minY) / 2, 4);
  minX = Math.max(0, minX - padX); minY = Math.max(0, minY - padY);
  maxX = Math.min(W, maxX + padX); maxY = Math.min(H, maxY + padY);
  return [Math.round(minX), Math.round(minY), Math.round(maxX - minX), Math.round(maxY - minY)];
}

const boxesOverlap = (a: BBox, b: [number, number, number, number]) =>
  a[0] <= b[0] + b[2] && a[2] >= b[0] && a[1] <= b[1] + b[3] && a[3] >= b[1];

/** County reference lines and city labels that fall inside the viewport — the detail that makes a
 *  zoomed-in map legible. County lines appear once zoomed past the whole-region view; city labels are
 *  the highest-population cities in frame, de-duplicated by distance so they never pile up. */
function mapContext(vp: [number, number, number, number]) {
  const [x, y, w, h] = vp;
  const countyLines: string[] = [];
  if (w < 340) { // below a ~1,050-mile frame — county lines would be a solid mesh at the national view
    for (const c of Object.values(GEO_COUNTIES.counties)) {
      if (boxesOverlap(c.b, vp)) { countyLines.push(c.path); if (countyLines.length >= 600) break; }
    }
  }
  const fontSize = Math.max(2, Math.round(w * 0.024 * 10) / 10);
  const dotR = Math.max(0.4, w * 0.006);
  const labels: GeoRender['labels'] = [];
  const boxes: BBox[] = []; // reject a label whose dot+text box overlaps one already placed
  const padY = fontSize * 0.7, gap = fontSize * 0.35;
  for (const [name, , cx, cy] of GEO_CITIES.cities) { // pop-sorted, so the biggest cities in view win
    if (labels.length >= 9) break;
    if (cx < x || cx > x + w || cy < y || cy > y + h) continue;
    const textW = name.length * fontSize * 0.55;
    const box: BBox = [cx - gap, cy - padY, cx + dotR + fontSize * 0.3 + textW + gap, cy + padY];
    // Skip labels whose text would spill past the frame's right or vertical edges — a clipped name
    // reads as a rendering bug. (The dot may sit near the left edge; only its text extent matters.)
    if (box[2] > x + w || box[1] < y || box[3] > y + h) continue;
    if (boxes.some((b) => box[0] < b[2] && box[2] > b[0] && box[1] < b[3] && box[3] > b[1])) continue;
    boxes.push(box);
    labels.push({ x: cx, y: cy, text: name });
  }
  return { countyLines, labels, fontSize, dotR };
}

/** Convex hull of projected [x,y] points (Andrew's monotone chain) → an SVG fill path, for the area a
 *  city/county/DMA's resolved ZIPs cover. */
function hullPath(points: Array<[number, number]>): { d: string; bbox: BBox } | null {
  const p = [...new Map(points.map((pt) => [`${pt[0]},${pt[1]}`, pt])).values()].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return null;
  const cross = (o: number[], a: number[], b: number[]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (src: Array<[number, number]>) => { const h: Array<[number, number]> = []; for (const pt of src) { while (h.length >= 2 && cross(h[h.length - 2], h[h.length - 1], pt) <= 0) h.pop(); h.push(pt); } h.pop(); return h; };
  const hull = half(p).concat(half([...p].reverse()));
  const d = 'M' + hull.map(([x, y]) => `${x},${y}`).join('L') + 'Z';
  return { d, bbox: pathBBox(d)! };
}

function findState(key: string) {
  const k = key.trim().toUpperCase();
  for (const [fips, s] of Object.entries(GEO_BASE.states)) {
    if (s.abbr === k || fips === k || s.name.toUpperCase() === k) return s;
  }
  return null;
}

export interface ChoroplethCell { d: string; intensity: number; abbr: string; value: number }
export interface StateChoropleth {
  viewBox: string;
  /** Every state outline, drawn as the empty base. */
  basePaths: string[];
  nationOutline: string;
  /** One filled path per state that has a value, `intensity` 0..1 against the busiest state. */
  cells: ChoroplethCell[];
}

/**
 * A US state choropleth: each state shaded by its share of the busiest state's value.
 *
 * Reuses the same state geometry as the targeting map, but grades the fill by DATA rather than
 * highlighting a targeted set — for "where did impressions come from". Accepts USPS / FIPS / full
 * name keys; anything that does not resolve to a state (e.g. "Undisclosed") is skipped. The block
 * renders the fills against the theme colour, so only the geometry and intensities live here.
 */
export function buildStateChoropleth(regions: Array<{ state: string; value: number }>): StateChoropleth {
  const [W, H] = GEO_BASE.viewBox;
  const basePaths = Object.values(GEO_BASE.states).map((s) => s.path);
  const byAbbr = new Map<string, number>();
  for (const r of regions) {
    const st = findState(r.state);
    if (!st) continue;
    byAbbr.set(st.abbr, (byAbbr.get(st.abbr) ?? 0) + (Number(r.value) || 0));
  }
  const max = Math.max(0, ...byAbbr.values());
  const cells: ChoroplethCell[] = [];
  if (max > 0) {
    for (const s of Object.values(GEO_BASE.states)) {
      const value = byAbbr.get(s.abbr) ?? 0;
      if (value > 0) cells.push({ d: s.path, abbr: s.abbr, value, intensity: value / max });
    }
  }
  return { viewBox: `0 0 ${W} ${H}`, basePaths, nationOutline: GEO_BASE.nationPath, cells };
}

export function buildGeoRender(mode: GeoMode, input: GeoInput): GeoRender {
  const [W, H] = GEO_BASE.viewBox;
  const basePaths = Object.values(GEO_BASE.states).map((s) => s.path);
  const fills: string[] = [];
  const circles: GeoRender['circles'] = [];
  let nationOutline: string | undefined;
  let bbox: BBox | null = null;
  let requested = 0, resolved = 0;

  if (mode === 'country') {
    const ctx = mapContext([0, 0, W, H]);
    return {
      viewBox: `0 0 ${W} ${H}`, basePaths, countyLines: ctx.countyLines, fills,
      nationOutline: GEO_BASE.nationPath, circles, labels: ctx.labels, fontSize: ctx.fontSize, dotR: ctx.dotR,
      requested: 1, resolved: 1,
    };
  }

  const zipCount = input.zips?.length ?? 0;
  const isArea = (mode === 'city' || mode === 'county' || mode === 'dma') && zipCount > 0;
  if (isArea) {
    // A city/DMA/county area the portal resolved to ZIPs: draw the convex hull of the ZIP centroids as
    // one region (matching the Mapbox map), rather than a swarm of circles.
    const pts = (input.zips ?? []).map((z) => GEO_ZIPS.zipXY[z.trim()]).filter(Boolean) as Array<[number, number]>;
    requested = 1; resolved = pts.length ? 1 : 0;
    const hull = hullPath(pts);
    if (hull) { fills.push(hull.d); bbox = merge(bbox, hull.bbox); }
  } else if (mode === 'zip') {
    const r = (input.radiusMiles && input.radiusMiles > 0 ? input.radiusMiles : 5) * GEO_BASE.milesToPx;
    for (const z of input.zips ?? []) {
      requested++; const p = GEO_ZIPS.zipXY[z.trim()];
      if (p) { resolved++; circles.push({ cx: p[0], cy: p[1], r }); bbox = merge(bbox, [p[0] - r, p[1] - r, p[0] + r, p[1] + r]); }
    }
  } else if (mode === 'state') {
    for (const k of input.states ?? []) {
      requested++; const st = findState(k);
      if (st) { resolved++; fills.push(st.path); bbox = merge(bbox, pathBBox(st.path)); }
    }
  } else if (mode === 'county') {
    for (const k of input.counties ?? []) {
      requested++; const c = GEO_COUNTIES.counties[k.trim()];
      if (c) { resolved++; fills.push(c.path); bbox = merge(bbox, pathBBox(c.path)); }
    }
  } else if (mode === 'dma') {
    for (const k of input.dmas ?? []) {
      requested++;
      const key = k.trim();
      const dma = GEO_DMA.dmas[key] ?? Object.values(GEO_DMA.dmas).find((d) => d.name.toLowerCase() === key.toLowerCase());
      if (dma) { resolved++; fills.push(dma.path); bbox = merge(bbox, pathBBox(dma.path)); }
    }
  } else if (mode === 'city') {
    const r = (input.radiusMiles && input.radiusMiles > 0 ? input.radiusMiles : 15) * GEO_BASE.milesToPx;
    for (const cRaw of input.cities ?? []) {
      requested++;
      const p = CITY_INDEX.get(cRaw.trim().toLowerCase().replace(/\s*,\s*/, '|'));
      if (p) { resolved++; circles.push({ cx: p[0], cy: p[1], r }); bbox = merge(bbox, [p[0] - r, p[1] - r, p[0] + r, p[1] + r]); }
    }
  }

  // Point-ish views (ZIPs/cities) zoom in close; a resolved area or region fill keeps more context.
  const minSpan = (mode === 'zip' || mode === 'city') && !isArea ? 22 : 90;
  const vp = viewportFor(bbox, W, H, minSpan);
  const ctx = mapContext(vp);
  return {
    viewBox: `${vp[0]} ${vp[1]} ${vp[2]} ${vp[3]}`,
    basePaths, countyLines: ctx.countyLines, fills, nationOutline, circles,
    labels: ctx.labels, fontSize: ctx.fontSize, dotR: ctx.dotR, requested, resolved,
  };
}
